import { forwardRef, type InputHTMLAttributes } from 'react'
import { cn } from '@/lib/utils'
import { AlertCircle } from 'lucide-react'

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: string
  error?: string
  hint?: string
}

const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ className, label, error, hint, id, ...props }, ref) => {
    const inputId = id || label?.toLowerCase().replace(/\s+/g, '-')

    return (
      <div className="w-full">
        {label && (
          <label
            htmlFor={inputId}
            className="block text-[var(--font-size-kid-base)] font-semibold text-[var(--color-kids-text)] mb-2"
          >
            {label}
            {props.required && <span className="text-[var(--color-kids-danger)] ml-1">*</span>}
          </label>
        )}
        <input
          ref={ref}
          id={inputId}
          className={cn(
            `
            w-full
            px-4 py-3
            min-h-[44px]
            text-[var(--font-size-kid-base)]
            font-body
            bg-white
            border-2
            rounded-[var(--radius-kid)]
            transition-all duration-200
            placeholder:text-[var(--color-kids-text-light)]
            focus:outline-none focus:ring-4
            disabled:opacity-50 disabled:cursor-not-allowed
          `,
            error
              ? 'border-[var(--color-kids-danger)] focus:border-[var(--color-kids-danger)] focus:ring-[var(--color-kids-danger)]/20'
              : 'border-[var(--color-kids-primary)]/20 focus:border-[var(--color-kids-primary)] focus:ring-[var(--color-kids-primary)]/20',
            className
          )}
          {...props}
        />
        {error && (
          <div className="flex items-center gap-2 mt-2 text-[var(--color-kids-danger)]">
            <AlertCircle className="w-4 h-4" />
            <span className="text-sm">{error}</span>
          </div>
        )}
        {hint && !error && (
          <p className="mt-2 text-sm text-[var(--color-kids-text-muted)]">{hint}</p>
        )}
      </div>
    )
  }
)

Input.displayName = 'Input'

export { Input }
